import React from 'react';
import { Box, Card, CardContent, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { CaseAssumptions, CaseAssumptionDefaults, CaseAssumptionsUpdate } from '../../../types/advisor';
import {
  CaseAssumptionPath,
  PATH_LABELS,
  PerCaseBlock,
  RATE_LABELS,
  UI_BLOCKS,
  visibleKeys,
} from '../../../types/planningAssumptions';

const pct = (n: number | undefined) => (n == null ? '—' : `${n}%`);

/** What a blank field falls through to, and where that comes from. */
export interface Fallback {
  text: string;
  label: string;
}

export type FallbackMap = Record<PerCaseBlock, Record<string, Fallback>>;

/** Card copy per editable block (the catalog decides which blocks and keys exist). */
const BLOCK_COPY: Record<PerCaseBlock, { title: string; body: string; footer?: string }> = {
  taxBucketReturnRates: {
    title: 'Account returns by tax bucket',
    body: "The expected return stamped on each account when a case is created, by the account's tax treatment. Leave a field blank to keep the fallback shown beneath it.",
    footer:
      'Applies to plans created from this dashboard, the client webform and the partner API. The Zoho intake uses PlanScout’s fixed assumptions and is not affected. Existing SIPS cases are not changed.',
  },
  colaRates: {
    title: 'Cost-of-living adjustments & inflation',
    body: "Annual COLA applied to each income stream. The target-income COLA is the plan's inflation rate.",
  },
  nqTaxRates: {
    title: 'Taxable (NQ) account taxes',
    body: 'Dividend yield and annual turnover SIPS applies to every taxable account when the plan runs. PlanScout default 2% / 30%.',
  },
};

/**
 * Per-key fallbacks for the editor: the inherited layer when it sets the key
 * (firm → branch), else the platform baseline of each path the block applies to —
 * equal values collapse ("5%"), differing ones are listed ("API 5% · dashboard 6%"). The Zoho
 * intake is not a path here: it uses PlanScout's fixed set and takes no overrides.
 */
export function buildFallbacks(
  inherited: CaseAssumptions | null | undefined,
  defaults: CaseAssumptionDefaults,
  inheritedLabel = 'Firm default'
): FallbackMap {
  const out = {} as FallbackMap;
  for (const block of UI_BLOCKS) {
    const name = block.name as PerCaseBlock;
    out[name] = {};
    for (const key of block.keys) {
      const inh = (inherited as any)?.[name]?.[key] as number | undefined;
      if (inh != null) {
        out[name][key] = { text: pct(inh), label: inheritedLabel };
        continue;
      }
      const groups: { paths: CaseAssumptionPath[]; value: number | undefined }[] = [];
      for (const path of block.paths) {
        const v = (defaults as any)?.[path]?.[name]?.[key] as number | undefined;
        const g = groups.find((x) => x.value === v);
        if (g) g.paths.push(path);
        else groups.push({ paths: [path], value: v });
      }
      const text =
        groups.length <= 1
          ? pct(groups[0]?.value)
          : groups.map((g) => `${g.paths.map((p) => PATH_LABELS[p]).join('/')} ${pct(g.value)}`).join(' · ');
      out[name][key] = { text, label: 'PlanScout default' };
    }
  }
  return out;
}

interface CaseAssumptionsFormProps {
  /** The layer being edited — only the keys set at this layer. */
  value: CaseAssumptions;
  onChange: (next: CaseAssumptions) => void;
  fallbacks: FallbackMap;
  disabled?: boolean;
}

/**
 * Controlled form for one layer's case assumptions (no fetching — used by the
 * firm and advisor editors). Blank = inherit (the key is omitted from the value);
 * 0 is a real value. Each field shows what applies when left blank. Only the
 * catalog's UI blocks are shown (the intake-only account-type returns are not).
 */
const CaseAssumptionsForm: React.FC<CaseAssumptionsFormProps> = ({ value, onChange, fallbacks, disabled }) => {
  const setRate = (block: PerCaseBlock, key: string, raw: string, max: number) => {
    const current = { ...((value[block] as Record<string, number> | undefined) || {}) };
    if (raw.trim() === '') {
      delete current[key];
    } else {
      const n = Number(raw);
      if (!Number.isFinite(n)) return;
      current[key] = Math.min(max, Math.max(0, n));
    }
    onChange({ ...value, [block]: current });
  };

  const field = (block: PerCaseBlock, key: string, max: number) => {
    const v = (value[block] as Record<string, number | undefined> | undefined)?.[key];
    const fallback = fallbacks[block]?.[key] ?? { text: '—', label: 'PlanScout default' };
    return (
      <Grid item xs={12} sm={6} key={`${block}.${key}`}>
        <TextField
          fullWidth
          size="small"
          label={RATE_LABELS[block][key] ?? key}
          type="number"
          value={v ?? ''}
          placeholder={fallback.text}
          onChange={(e) => setRate(block, key, e.target.value, max)}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          inputProps={{ step: 0.1, min: 0, max }}
          helperText={`${fallback.label}: ${fallback.text}`}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
    );
  };

  return (
    <Box component="fieldset" disabled={disabled} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
      {UI_BLOCKS.map((block, i) => {
        const name = block.name as PerCaseBlock;
        const copy = BLOCK_COPY[name];
        return (
          <Card variant="outlined" key={name} sx={{ mb: i < UI_BLOCKS.length - 1 ? 2 : 0 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
                {copy.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {copy.body}
              </Typography>
              <Grid container spacing={2}>
                {visibleKeys(block).map((key) => field(name, key, block.max))}
              </Grid>
              {copy.footer && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  {copy.footer}
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default CaseAssumptionsForm;

/** True when the layer sets at least one key in an editable block. */
export const hasAnyAssumptions = (a: CaseAssumptions | null | undefined): boolean =>
  UI_BLOCKS.some((b) => Object.keys((a as any)?.[b.name] || {}).length > 0);

/**
 * PUT body that makes the stored layer exactly `value` for the editable blocks
 * (empty block → cleared). The intake-only account-type block is never sent, so
 * anything stored there stays untouched.
 */
export const toUpdate = (value: CaseAssumptions): CaseAssumptionsUpdate => {
  const out: CaseAssumptionsUpdate = {};
  for (const b of UI_BLOCKS) {
    const name = b.name as PerCaseBlock;
    const block = (value[name] || {}) as Record<string, number>;
    (out as any)[name] = Object.keys(block).length > 0 ? block : null;
  }
  return out;
};
